import React from "react";
import { Loader2, PauseCircle, Sparkles, Download, Plus, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import EnrichPersonalSitesButton from "@/components/realtors/EnrichPersonalSitesButton";
import CheckPhotosButton from "@/components/realtors/CheckPhotosButton";
import FindTopPerformersButton from "@/components/realtors/FindTopPerformersButton";

/**
 * Primary action bar on the Realtors page (right-hand side of the page
 * header). Buttons all target either the current search/brokerage/tab
 * filter OR — when any rows are ticked — the selection only.
 *
 * Extracted from Realtors.jsx to keep the page-level file focused on data
 * loading + list rendering. Only presentation lives here; every mutation
 * still comes from the page (via the callback props).
 */
export default function RealtorsToolbar({
  isAdmin,
  someChecked,
  selectedCount,
  selectedIds,
  hasRealtors,
  enrichAllJob,
  onEnrichAll,
  onExport,
  onExportXlsx,
  onAdd,
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button
        variant="outline"
        size="sm"
        onClick={onEnrichAll}
        disabled={!hasRealtors || !!enrichAllJob}
        className="h-8 px-2.5 text-xs"
        data-testid="enrich-all-button"
        title={
          someChecked
            ? `Run social + web enrichment on the ${selectedCount} selected realtors`
            : "Run social + web enrichment on every realtor in the current filter"
        }
      >
        {enrichAllJob?.status === "paused" ? (
          <PauseCircle className="h-3.5 w-3.5 mr-1.5 text-amber-600" />
        ) : enrichAllJob ? (
          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5 mr-1.5" />
        )}
        {enrichAllJob?.status === "paused"
          ? "Paused"
          : enrichAllJob
            ? "Enriching…"
            : someChecked
              ? `Enrich ${selectedCount} selected`
              : "Enrich all"}
      </Button>
      <EnrichPersonalSitesButton selectedIds={selectedIds} />
      {isAdmin ? <CheckPhotosButton selectedIds={selectedIds} /> : null}
      {isAdmin ? <FindTopPerformersButton /> : null}
      <Button
        variant="outline"
        size="sm"
        onClick={onExport}
        disabled={!hasRealtors}
        className="h-8 px-2.5 text-xs"
        data-testid="export-vcards-button"
        title={
          someChecked
            ? `Export the ${selectedCount} selected realtors as one .vcf file (drops into iOS/Android Contacts)`
            : "Export every realtor in the current filter as one .vcf file"
        }
      >
        <Download className="h-3.5 w-3.5 mr-1.5" />
        {someChecked ? `Export ${selectedCount}` : "Export all (filtered)"}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onExportXlsx}
        disabled={!hasRealtors}
        className="h-8 px-2.5 text-xs"
        data-testid="export-xlsx-button"
        title={
          someChecked
            ? `Export the ${selectedCount} selected realtors to an Excel spreadsheet (.xlsx)`
            : "Export every realtor in the current filter to an Excel spreadsheet (.xlsx)"
        }
      >
        <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
        {someChecked ? `Spreadsheet ${selectedCount}` : "Export spreadsheet"}
      </Button>
      <Button
        size="sm"
        onClick={onAdd}
        className="h-8 px-2.5 text-xs text-white"
        style={{ background: "hsl(var(--brand))" }}
        data-testid="add-realtor-button"
        title="Manually add a realtor (name + brokerage + contact info)"
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" /> Add realtor
      </Button>
    </div>
  );
}
